<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import axios from 'axios'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import { debounce } from 'lodash'
import { Head, Link, router } from '@inertiajs/vue3'
import { notify } from '@kyvg/vue3-notification'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import { useCopyText } from '@/Composables/useCopyText'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import {
    LayoutMode,
    buildDieline,
    computePlanoLayout,
    roundUpToMultiple,
    validateBoxDimensions,
} from '@/Composables/useCustomSizeLayout'
import FramePreview from '@/Pages/CustomSize/FramePreview.vue'
import PlanoLayoutPreview from '@/Pages/CustomSize/PlanoLayoutPreview.vue'
import { faCopy, faUndoAlt, faSave, faSyncAlt } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faCopy, faUndoAlt, faSave, faSyncAlt)

interface Plano {
    id: number
    full_name: string
    width: number
    length: number
    price: number
    supplier: string | null
}

interface Setting {
    plate_price: number
    print_price: number
    pound_price: number
    knife_price: number
    target_profit: number
    safe_margin: number
    material_rounding_multiple: number
    offer_note: string | null
}

interface PlanoSnapshot {
    id: number
    full_name: string
    width: number
    length: number
    price: number
    supplier: string | null
}

interface OfferInputs {
    box: Record<string, number | string>
    plano_paper_id: number | null
    plano: PlanoSnapshot | null
    safe_margin: number | string
    layout_mode: LayoutMode
    costs: Record<string, number | string>
    rounding_multiple: number | string
    material_cost_override: number | string | null
    quantity: number | string
}

interface Offer {
    id: number
    name: string
    status: string
    production_plan: string | null
    deal_date: string | null
    plano_paper_id: number | null
    inputs: Partial<OfferInputs>
    offer_text: string | null
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    planos: Plano[]
    setting: Setting
    offer: Offer | null
    default_name: string | null
    status_options: { value: string, label: string }[]
    production_plan_options: { value: string, label: string }[]
}>()

const toNumber = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : NaN)
const formatCm = (value: number) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format(value)
const formatRupiahPrecise = (value: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value)

const savedInputs: Partial<OfferInputs> = props.offer?.inputs ?? {}

const offerName = ref(props.offer?.name ?? props.default_name ?? '')
const offerStatus = ref(props.offer?.status ?? 'pending')
const productionPlan = ref<string | null>(props.offer?.production_plan ?? null)
const dealDate = ref(props.offer?.deal_date ?? '')
const hasProductionPlan = computed(() => offerStatus.value !== 'pending')
const isDeal = computed(() => offerStatus.value === 'deal')

const box = reactive({
    length: savedInputs.box?.length ?? 22,
    width: savedInputs.box?.width ?? 11,
    height: savedInputs.box?.height ?? 4.5,
})

const boxFields: { key: keyof typeof box, label: string }[] = [
    { key: 'length', label: 'Panjang (P)' },
    { key: 'width', label: 'Lebar (L)' },
    { key: 'height', label: 'Tinggi (T)' },
]

const defaultCosts = () => ({
    plate_price: props.setting.plate_price,
    print_price: props.setting.print_price,
    pound_price: props.setting.pound_price,
    knife_price: props.setting.knife_price,
    target_profit: props.setting.target_profit,
})

const costs = reactive<Record<string, number | string>>({ ...defaultCosts(), ...savedInputs.costs })
const costFields = [
    { key: 'plate_price', label: 'Harga plat' },
    { key: 'print_price', label: 'Harga cetak' },
    { key: 'pound_price', label: 'Harga pound' },
    { key: 'knife_price', label: 'Harga pisau' },
    { key: 'target_profit', label: 'Target profit' },
]

const copyPlano = (plano: Plano | PlanoSnapshot): PlanoSnapshot => ({
    id: plano.id,
    full_name: plano.full_name,
    width: plano.width,
    length: plano.length,
    price: plano.price,
    supplier: plano.supplier,
})

const findLatestPlano = (id: number | null | undefined) => props.planos.find((plano) => plano.id === id) ?? null

const initialPlano = (): PlanoSnapshot | null => {
    if (savedInputs.plano) return copyPlano(savedInputs.plano)
    const latestPlano = findLatestPlano(props.offer ? props.offer.plano_paper_id : props.planos[0]?.id)

    return latestPlano ? copyPlano(latestPlano) : null
}

const planoSnapshot = ref<PlanoSnapshot | null>(initialPlano())
const selectedPlanoId = computed(() => planoSnapshot.value?.id ?? null)
const latestSelectedPlano = computed(() => findLatestPlano(selectedPlanoId.value))
const isSelectedPlanoDeleted = computed(() => !!planoSnapshot.value && !latestSelectedPlano.value)

const isSelectedPlanoOutdated = computed(() => {
    if (!planoSnapshot.value || !latestSelectedPlano.value) return false

    return JSON.stringify(copyPlano(latestSelectedPlano.value)) !== JSON.stringify(planoSnapshot.value)
})

const planoOptions = computed(() => [
    ...(isSelectedPlanoDeleted.value && planoSnapshot.value
        ? [{ value: planoSnapshot.value.id, label: `${planoSnapshot.value.full_name} (tersimpan, sudah tidak ada di daftar)`, disabled: true }]
        : []),
    ...props.planos.map((plano) => ({
        value: plano.id,
        label: `${plano.full_name} — ${formatCm(plano.width)} × ${formatCm(plano.length)} cm — ${useFormatCurrency(plano.price)}${plano.supplier ? ` (${plano.supplier})` : ''}`,
        disabled: false,
    })),
])

const layoutModeOptions: { value: LayoutMode, label: string }[] = [
    { value: 'auto', label: 'Otomatis (campuran terbanyak)' },
    { value: 'horizontal', label: 'Semua horizontal' },
    { value: 'vertical', label: 'Semua vertikal' },
]

const selectPlano = (id: number) => {
    const latestPlano = findLatestPlano(id)
    planoSnapshot.value = latestPlano ? copyPlano(latestPlano) : null
}

const refreshPlano = () => {
    if (latestSelectedPlano.value) {
        planoSnapshot.value = copyPlano(latestSelectedPlano.value)
    }
}
const safeMargin = ref<number | string>(savedInputs.safe_margin ?? props.setting.safe_margin)
const roundingMultiple = ref<number | string>(savedInputs.rounding_multiple ?? props.setting.material_rounding_multiple)
const materialCostOverride = ref<number | string | null>(savedInputs.material_cost_override ?? null)
const layoutMode = ref<LayoutMode>(savedInputs.layout_mode ?? 'auto')
const quantity = ref<number | string>(savedInputs.quantity ?? 1000)

const resetToSettings = () => {
    Object.assign(costs, defaultCosts())
    safeMargin.value = props.setting.safe_margin
    roundingMultiple.value = props.setting.material_rounding_multiple
    materialCostOverride.value = null
}

const boxDimensions = computed(() => ({
    length: toNumber(box.length),
    width: toNumber(box.width),
    height: toNumber(box.height),
}))

const boxErrors = computed(() => validateBoxDimensions(boxDimensions.value))
const dieline = computed(() => buildDieline(boxDimensions.value))
const safeMarginValue = computed(() => toNumber(safeMargin.value))
const isSafeMarginValid = computed(() => safeMarginValue.value >= 0)

const selectedPlano = computed(() => planoSnapshot.value)

const layout = computed(() => {
    if (!dieline.value || !selectedPlano.value || !isSafeMarginValid.value) return null

    return computePlanoLayout(
        selectedPlano.value.width,
        selectedPlano.value.length,
        dieline.value.width,
        dieline.value.height,
        safeMarginValue.value,
        layoutMode.value,
    )
})

const unusedArea = computed(() => {
    if (!layout.value || !dieline.value || !selectedPlano.value) return null
    const planoArea = selectedPlano.value.width * selectedPlano.value.length
    const frameArea = layout.value.count * dieline.value.width * dieline.value.height
    const cellArea = layout.value.count * layout.value.cellWidth * layout.value.cellHeight

    return {
        total: Math.max(0, (1 - frameArea / planoArea) * 100),
        leftover: Math.max(0, (1 - cellArea / planoArea) * 100),
    }
})

const costValues = computed(() => Object.fromEntries(
    costFields.map((field) => [field.key, toNumber(costs[field.key])])
) as Record<string, number>)

const invalidCostLabels = computed(() => costFields
    .filter((field) => !(costValues.value[field.key] >= 0))
    .map((field) => field.label))

const materialCostRaw = computed(() => {
    if (!layout.value?.count || !selectedPlano.value) return null

    return selectedPlano.value.price / layout.value.count
})

const roundingMultipleValue = computed(() => Math.max(0, toNumber(roundingMultiple.value) || 0))

const materialCostAuto = computed(() => materialCostRaw.value === null
    ? null
    : roundUpToMultiple(materialCostRaw.value, roundingMultipleValue.value))

const isMaterialCostOverridden = computed(() => materialCostOverride.value !== null)
const isMaterialCostValid = computed(() => !isMaterialCostOverridden.value || toNumber(materialCostOverride.value) >= 0)

const materialCostInput = computed({
    get: () => materialCostOverride.value ?? materialCostAuto.value ?? '',
    set: (value: number | string) => {
        materialCostOverride.value = value
    },
})

const materialCostRounded = computed(() => {
    if (!isMaterialCostOverridden.value) return materialCostAuto.value

    return isMaterialCostValid.value ? toNumber(materialCostOverride.value) : null
})

const highlightedCostKey = ref<string | null>(null)
const highlightedInputClass = '!border-indigo-500 !bg-indigo-50 ring-2 ring-indigo-400'

const formatTermValue = (value: number | null) => value !== null && value >= 0 ? useFormatCurrency(value) : '-'

const sellingPriceTerms = computed(() => [
    { key: 'material', label: 'Bahan', value: formatTermValue(materialCostRounded.value) },
    ...costFields.map((field) => ({
        key: field.key,
        label: field.label.replace('Harga ', ''),
        value: formatTermValue(costValues.value[field.key]),
    })),
])

const sellingPrice = computed(() => {
    if (materialCostRounded.value === null || invalidCostLabels.value.length) return null

    return materialCostRounded.value + costFields.reduce((total, field) => total + costValues.value[field.key], 0)
})

const quantityValue = computed(() => Math.max(0, Math.floor(toNumber(quantity.value) || 0)))
const sheetsNeeded = computed(() => layout.value?.count ? Math.ceil(quantityValue.value / layout.value.count) : 0)

const boxSizeLabel = computed(() => {
    const { length, width, height } = boxDimensions.value
    if (boxErrors.value.length) return ''

    return `${formatCm(length)} × ${formatCm(width)} × ${formatCm(height)} cm`
})

const offerNote = (props.setting.offer_note ?? '').trim()

const generatedOfferText = computed(() => [
    'Berikut merupakan penawaran kami kak:',
    '',
    `Untuk ukuran ${boxSizeLabel.value || '...'}`,
    selectedPlano.value?.full_name ?? '...',
    '',
    `1000 pcs: ${sellingPrice.value === null ? 'Rp .../pcs' : `Rp ${useFormatNumber(sellingPrice.value)}/pcs`}`,
    '2000 pcs: Rp .../pcs',
    '3000 pcs: Rp .../pcs',
    '',
    'Catatan:',
    offerNote,
].join('\n').trimEnd())

const offerText = ref(props.offer?.offer_text ?? generatedOfferText.value)
const regenerateOfferText = () => {
    offerText.value = generatedOfferText.value
}
watch(generatedOfferText, regenerateOfferText)

const offerPayload = computed(() => ({
    name: offerName.value.trim(),
    status: offerStatus.value,
    production_plan: hasProductionPlan.value ? productionPlan.value : null,
    deal_date: isDeal.value && dealDate.value ? dealDate.value : null,
    plano_paper_id: latestSelectedPlano.value?.id ?? null,
    box_size: boxSizeLabel.value || null,
    frames_per_plano: layout.value?.count ?? null,
    selling_price: sellingPrice.value,
    offer_text: offerText.value,
    inputs: {
        box: { ...box },
        plano_paper_id: selectedPlanoId.value,
        plano: planoSnapshot.value,
        safe_margin: safeMargin.value,
        layout_mode: layoutMode.value,
        costs: { ...costs },
        rounding_multiple: roundingMultiple.value,
        material_cost_override: materialCostOverride.value,
        quantity: quantity.value,
    } as OfferInputs,
}))

const AUTOSAVE_DELAY_MS = 800

const serializedPayload = computed(() => JSON.stringify(offerPayload.value))
const lastSavedSnapshot = ref(props.offer ? serializedPayload.value : null)
const hasUnsavedChanges = computed(() => serializedPayload.value !== lastSavedSnapshot.value)
const initialSnapshot = serializedPayload.value
const hasLosableChanges = computed(() => props.offer ? hasUnsavedChanges.value : serializedPayload.value !== initialSnapshot)
const isSaving = ref(false)
const saveErrors = ref<Record<string, string>>({})
const autosaveFailed = ref(false)
const lastSavedAt = ref<Date | null>(null)

let activeSave: Promise<void> | null = null

const autosaveOffer = async (): Promise<void> => {
    if (!props.offer) return
    if (activeSave) {
        await activeSave
        return autosaveOffer()
    }
    if (!hasUnsavedChanges.value) return

    const snapshot = serializedPayload.value
    isSaving.value = true

    activeSave = axios.patch(route('custom-size.offers.update', props.offer.id), JSON.parse(snapshot))
        .then(() => {
            lastSavedSnapshot.value = snapshot
            lastSavedAt.value = new Date()
            saveErrors.value = {}
            autosaveFailed.value = false
        })
        .catch((error) => {
            autosaveFailed.value = true
            const errors = error.response?.data?.errors ?? {}
            saveErrors.value = Object.fromEntries(
                Object.entries(errors).map(([key, messages]) => [key, (messages as string[])[0]])
            )
        })
        .finally(() => {
            isSaving.value = false
            activeSave = null
        })

    await activeSave

    if (!autosaveFailed.value && hasUnsavedChanges.value) {
        await autosaveOffer()
    }
}

const debouncedAutosave = debounce(autosaveOffer, AUTOSAVE_DELAY_MS)

watch(serializedPayload, () => {
    if (props.offer && hasUnsavedChanges.value) debouncedAutosave()
})

const flushAutosave = async () => {
    debouncedAutosave.cancel()
    await autosaveOffer()
}

const warnBeforeUnload = (event: BeforeUnloadEvent) => {
    if (hasLosableChanges.value) event.preventDefault()
}

onMounted(() => window.addEventListener('beforeunload', warnBeforeUnload))
onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', warnBeforeUnload)
    if (props.offer && hasUnsavedChanges.value) flushAutosave()
})

const autosaveStatus = computed(() => {
    if (isSaving.value) return { text: 'Menyimpan…', class: 'text-gray-500' }
    if (autosaveFailed.value) return { text: 'Gagal menyimpan otomatis', class: 'text-red-500' }
    if (hasUnsavedChanges.value) return { text: 'Menunggu disimpan…', class: 'text-amber-600' }
    if (lastSavedAt.value) {
        return { text: `Tersimpan otomatis ${lastSavedAt.value.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}`, class: 'text-gray-400' }
    }

    return { text: 'Semua perubahan tersimpan', class: 'text-gray-400' }
})

const isOfferNoteOutdated = computed(() => !!offerNote && !offerText.value.includes(offerNote))

const noteSettingsUrl = computed(() =>
    `${route('custom-size.settings.edit', { back: window.location.pathname + window.location.search })}#catatan`
)

const goToNoteSettings = async () => {
    if (props.offer) {
        await flushAutosave()
        if (autosaveFailed.value && !window.confirm('Perubahan gagal disimpan dan akan hilang. Lanjut ke Settings?')) return
    } else if (hasLosableChanges.value && !window.confirm('Penawaran ini belum disimpan dan isiannya akan hilang. Lanjut ke Settings?')) {
        return
    }

    router.visit(noteSettingsUrl.value)
}

const createOffer = () => {
    router.post(route('custom-size.offers.store'), offerPayload.value, {
        preserveScroll: true,
        onStart: () => {
            isSaving.value = true
            saveErrors.value = {}
        },
        onSuccess: () => notify({ title: 'Tersimpan', text: 'Penawaran berhasil dibuat.', type: 'success' }),
        onError: (errors: Record<string, string>) => {
            saveErrors.value = errors
        },
        onFinish: () => {
            isSaving.value = false
        },
    })
}

const detailRows = computed(() => {
    if (!selectedPlano.value || !layout.value || !dieline.value) return []

    return [
        { label: 'Jenis kertas plano', value: selectedPlano.value.full_name },
        { label: 'Ukuran plano', value: `${formatCm(selectedPlano.value.width)} × ${formatCm(selectedPlano.value.length)} cm` },
        { label: 'Ukuran kemasan (P × L × T)', value: boxSizeLabel.value },
        { label: 'Ukuran kerangka (bentangan)', value: `${formatCm(dieline.value.width)} × ${formatCm(dieline.value.height)} cm` },
        { label: 'Jarak aman per sisi', value: `${formatCm(safeMarginValue.value)} cm` },
        { label: 'Kerangka per plano', value: `${layout.value.count} kerangka` },
    ]
})
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="flex flex-wrap items-end gap-3 border-b border-gray-200 bg-white px-4 py-4">
        <div class="w-full max-w-md">
            <label class="mb-1 block text-xs text-gray-500">Nama penawaran</label>
            <input v-model="offerName" :placeholder="default_name ?? offer?.name ?? 'Penawaran'"
                class="w-full rounded-md border-gray-300 text-sm" :class="saveErrors.name ? 'errorShake' : ''" />
            <div v-if="saveErrors.name" class="mt-1 text-xs text-red-500">{{ saveErrors.name }}</div>
        </div>
        <template v-if="offer">
            <span class="flex items-center gap-x-2 pb-2 text-xs" :class="autosaveStatus.class">
                {{ autosaveStatus.text }}
                <button v-if="autosaveFailed" type="button" class="underline hover:text-red-600"
                    @click="flushAutosave">Coba lagi</button>
            </span>
        </template>
        <template v-else>
            <Button type="save" icon="fal fa-save" :loading="isSaving" label="Simpan penawaran" @click="createOffer" />
            <span class="pb-2 text-xs text-gray-400">
                Penawaran belum disimpan. Setelah disimpan, perubahan berikutnya tersimpan otomatis.
            </span>
        </template>

        <div class="flex w-full flex-wrap items-start gap-x-6 gap-y-3">
            <div>
                <label class="mb-1 block text-xs text-gray-500">Status penawaran</label>
                <div class="flex flex-wrap gap-2">
                    <button v-for="option in status_options" :key="option.value" type="button"
                        class="rounded-md px-3 py-1.5 text-sm font-medium"
                        :class="offerStatus === option.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'"
                        @click="offerStatus = option.value">
                        {{ option.label }}
                    </button>
                </div>
            </div>

            <div v-if="isDeal">
                <label class="mb-1 block text-xs text-gray-500">Tanggal deal (opsional)</label>
                <input v-model="dealDate" type="date" class="rounded-md border-gray-300 py-1.5 text-sm"
                    :class="saveErrors.deal_date ? 'errorShake' : ''" />
            </div>

            <div v-if="hasProductionPlan">
                <label class="mb-1 block text-xs text-gray-500">Rencana produksi</label>
                <div class="flex flex-wrap gap-2">
                    <button v-for="option in production_plan_options" :key="option.value" type="button"
                        class="rounded-md px-3 py-1.5 text-sm"
                        :class="productionPlan === option.value ? 'bg-indigo-100 text-indigo-700 ring-1 ring-inset ring-indigo-400' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'"
                        @click="productionPlan = productionPlan === option.value ? null : option.value">
                        {{ option.label }}
                    </button>
                </div>
            </div>
        </div>
        <div v-if="Object.keys(saveErrors).filter((key) => key !== 'name').length" class="w-full text-xs text-red-500">
            {{ Object.entries(saveErrors).filter(([key]) => key !== 'name').map(([, message]) => message).join(' ') }}
        </div>
    </div>

    <div class="grid gap-4 px-4 py-6 xl:grid-cols-5">
        <div class="flex flex-col gap-4 xl:col-span-2">
            <section class="rounded-xl border border-gray-200 bg-white p-5">
                <div class="mb-3">
                    <h3 class="font-semibold text-gray-700">Ukuran kemasan</h3>
                    <p class="text-xs text-gray-400">
                        Dalam cm. Panjang (P) jadi badan, lebar (L) jadi panel, tinggi (T) jadi samping
                        sekaligus penutup atas & bawah dan lidah lem.
                    </p>
                </div>
                <div class="grid grid-cols-3 gap-3">
                    <div v-for="field in boxFields" :key="field.key">
                        <label class="mb-1 flex items-center gap-x-1.5 text-xs text-gray-500">
                            <span class="inline-block size-2.5 rounded-sm border border-green-900 bg-[#00a14b]" />
                            {{ field.label }}
                        </label>
                        <InputNumber showButtons :step="0.5" v-model="box[field.key]" :min="0" :maxFractionDigits="2" locale="id-ID"
                            suffix=" cm" fluid inputClass="!border-[#00a14b]/60 !bg-[#00a14b]/10" />
                    </div>
                </div>
            </section>

            <section class="rounded-xl border border-gray-200 bg-white p-5">
                <div class="mb-3">
                    <h3 class="font-semibold text-gray-700">Kertas plano & susunan</h3>
                </div>

                <div v-if="!planos.length"
                    class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                    Belum ada data kertas plano.
                    <Link :href="route('custom-size.planos.index')" class="primaryLink">Tambah kertas plano</Link>
                </div>

                <div v-else class="flex flex-col gap-3">
                    <div>
                        <label class="mb-1 flex items-center gap-x-1.5 text-xs text-gray-500">
                            <span class="inline-block size-2.5 rounded-sm border border-pink-600 bg-pink-300" />
                            Kertas plano
                        </label>
                        <Select :modelValue="selectedPlanoId" :options="planoOptions" optionLabel="label"
                            optionValue="value" optionDisabled="disabled" placeholder="Pilih kertas plano" filter
                            fluid class="!border-pink-400 !bg-pink-100" @update:modelValue="selectPlano" />

                        <div v-if="planoSnapshot" class="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                            <div class="flex items-center justify-between gap-x-2">
                                <span class="font-medium text-gray-700">Data plano yang dipakai penawaran ini</span>
                                <Button v-if="isSelectedPlanoOutdated" size="xxs" type="secondary" icon="fal fa-sync-alt"
                                    label="Refresh" v-tooltip="'Ambil data terbaru dari Kertas Plano'"
                                    @click="refreshPlano" />
                            </div>
                            <div class="mt-1">
                                {{ formatCm(planoSnapshot.width) }} × {{ formatCm(planoSnapshot.length) }} cm ·
                                {{ useFormatCurrency(planoSnapshot.price) }}{{ planoSnapshot.supplier ? ` · ${planoSnapshot.supplier}` : '' }}
                            </div>
                            <div v-if="isSelectedPlanoOutdated && latestSelectedPlano" class="mt-1 text-amber-600">
                                Data di Kertas Plano sudah berubah:
                                {{ latestSelectedPlano.full_name }},
                                {{ formatCm(latestSelectedPlano.width) }} × {{ formatCm(latestSelectedPlano.length) }} cm,
                                {{ useFormatCurrency(latestSelectedPlano.price) }}.
                                Klik Refresh untuk memakai data terbaru.
                            </div>
                            <div v-else-if="isSelectedPlanoDeleted" class="mt-1 text-gray-400">
                                Plano ini sudah dihapus dari daftar, data tersimpan tetap dipakai.
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="mb-1 flex items-center gap-x-1.5 text-xs text-gray-500">
                                <span class="inline-block size-2.5 rounded-sm border border-yellow-700 bg-[#fff200]" />
                                Jarak aman per sisi
                            </label>
                            <InputNumber showButtons :step="0.1" v-model="safeMargin" :min="0" :maxFractionDigits="2" locale="id-ID"
                                suffix=" cm" fluid :class="isSafeMarginValid ? '' : 'errorShake'"
                                inputClass="!border-yellow-500 !bg-[#fff200]/30" />
                        </div>
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Susunan kerangka</label>
                            <Select v-model="layoutMode" :options="layoutModeOptions" optionLabel="label"
                                optionValue="value" fluid />
                        </div>
                    </div>
                </div>
            </section>

            <section class="rounded-xl border border-gray-200 bg-white p-5">
                <h3 class="mb-3 font-semibold text-gray-700">Detail</h3>
                <div v-if="!detailRows.length"
                    class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                    Lengkapi ukuran kemasan dan kertas plano
                </div>
                <dl v-else class="divide-y divide-gray-100 text-sm">
                    <div v-for="row in detailRows" :key="row.label" class="flex justify-between gap-x-4 py-1.5">
                        <dt class="text-gray-500">{{ row.label }}</dt>
                        <dd class="text-right font-medium text-gray-700">{{ row.value }}</dd>
                    </div>
                </dl>
            </section>

            <section class="rounded-xl border border-gray-200 bg-white p-5">
                <div class="mb-3 flex items-center justify-between gap-x-4">
                    <div>
                        <h3 class="font-semibold text-gray-700">Perhitungan</h3>
                        <p class="text-xs text-gray-400">
                            Harga awal dari Settings, perubahan di sini hanya berlaku untuk penawaran ini.
                        </p>
                    </div>
                    <Button size="xs" type="tertiary" icon="fal fa-undo-alt" label="Reset ke Settings"
                        @click="resetToSettings" />
                </div>

                <div class="grid gap-x-8 text-sm sm:grid-cols-2 xl:grid-cols-1">
                    <dl class="divide-y divide-gray-100">
                        <div class="flex items-center justify-between gap-x-4 py-1.5">
                            <dt class="text-gray-500">Harga per plano</dt>
                            <dd class="font-medium text-gray-700">
                                {{ selectedPlano ? useFormatCurrency(selectedPlano.price) : '-' }}
                            </dd>
                        </div>
                        <div class="flex items-center justify-between gap-x-4 py-1.5">
                            <dt class="text-gray-500">Bahan per kerangka</dt>
                            <dd class="font-medium text-gray-700">
                                {{ materialCostRaw === null ? '-' : formatRupiahPrecise(materialCostRaw) }}
                            </dd>
                        </div>
                        <div class="flex items-center justify-between gap-x-4 py-1.5">
                            <dt class="text-gray-500">
                                Bahan setelah pembulatan
                                <div v-if="isMaterialCostOverridden" class="text-xxs text-amber-600">
                                    Diubah manual, otomatis:
                                    {{ materialCostAuto === null ? '-' : useFormatCurrency(materialCostAuto) }} ·
                                    <button type="button" class="underline hover:text-amber-700"
                                        @click="materialCostOverride = null">pakai otomatis</button>
                                </div>
                                <div v-else class="text-xxs text-gray-400">
                                    Otomatis dibulatkan ke atas kelipatan {{ useFormatCurrency(roundingMultipleValue) }}
                                </div>
                            </dt>
                            <dd class="relative w-32 shrink-0">
                                <InputNumber showButtons :step="100" v-model="materialCostInput" mode="currency" currency="IDR" locale="id-ID"
                                    :maxFractionDigits="0" :min="0" fluid size="small"
                                    :class="isMaterialCostValid ? '' : 'errorShake'"
                                    :inputClass="['text-right font-semibold transition-shadow', highlightedCostKey === 'material' ? highlightedInputClass : '']" />
                            </dd>
                        </div>
                    </dl>

                    <dl class="divide-y divide-gray-100">
                        <div v-for="field in costFields" :key="field.key"
                            class="flex items-center justify-between gap-x-4 py-1.5">
                            <dt class="text-gray-500">{{ field.label }}</dt>
                            <dd class="relative w-32 shrink-0">
                                <InputNumber showButtons :step="100" v-model="costs[field.key]" mode="currency" currency="IDR" locale="id-ID"
                                    :maxFractionDigits="0" :min="0" fluid size="small"
                                    :class="costValues[field.key] >= 0 ? '' : 'errorShake'"
                                    :inputClass="['text-right transition-shadow', highlightedCostKey === field.key ? highlightedInputClass : '']" />
                            </dd>
                        </div>
                    </dl>
                </div>

                <div v-if="invalidCostLabels.length" class="mt-2 text-xs text-red-500">
                    Lengkapi biaya: {{ invalidCostLabels.join(', ') }}
                </div>

                <div class="mt-3 flex items-center justify-between rounded-lg bg-indigo-50 px-4 py-3">
                    <div>
                        <div class="text-sm text-indigo-700">Harga jual per pcs</div>
                        <div class="flex flex-wrap items-center gap-x-1 text-xxs text-indigo-400">
                            <template v-for="(term, index) in sellingPriceTerms" :key="term.key">
                                <span v-if="index">+</span>
                                <span class="cursor-default rounded px-1 transition-colors"
                                    :class="highlightedCostKey === term.key ? 'bg-indigo-200 text-indigo-800' : 'hover:bg-indigo-100'"
                                    @mouseenter="highlightedCostKey = term.key" @mouseleave="highlightedCostKey = null">
                                    {{ term.label }} {{ term.value }}
                                </span>
                            </template>
                        </div>
                    </div>
                    <div class="text-xl font-bold text-indigo-700">
                        {{ sellingPrice === null ? '-' : useFormatCurrency(sellingPrice) }}
                    </div>
                </div>

                <div class="mt-2 grid gap-2 text-sm text-gray-600 sm:grid-cols-3 xl:grid-cols-1">
                    <div class="flex items-center justify-between gap-x-2 rounded-lg border border-gray-100 px-3 py-1.5">
                        <span>Jumlah produksi</span>
                        <div class="w-32">
                            <InputNumber showButtons :step="100" v-model="quantity" :min="0" locale="id-ID" suffix=" pcs" fluid size="small"
                                inputClass="text-right" />
                        </div>
                    </div>
                    <div class="flex items-center rounded-lg border border-gray-100 px-3 py-1.5">
                        Kebutuhan plano:
                        <span class="ml-1 font-semibold">{{ layout?.count ? `${useFormatNumber(sheetsNeeded)} lembar` : '-' }}</span>
                    </div>
                    <div class="flex items-center rounded-lg border border-gray-100 px-3 py-1.5">
                        Total:
                        <span class="ml-1 font-semibold">
                            {{ sellingPrice === null ? '-' : useFormatCurrency(sellingPrice * quantityValue) }}
                        </span>
                    </div>
                </div>
            </section>
        </div>

        <div class="flex flex-col gap-4 xl:col-span-3">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                <span class="flex items-center gap-x-1.5"><span class="inline-block size-3 rounded-sm border border-pink-600 bg-pink-300" />Kertas plano</span>
                <span class="flex items-center gap-x-1.5"><span class="inline-block size-3 rounded-sm border border-green-900 bg-[#00a14b]" />Kerangka / jaring-jaring kemasan</span>
                <span class="flex items-center gap-x-1.5"><span class="inline-block size-3 rounded-sm border border-yellow-700 bg-[#fff200]" />Jarak aman (setiap kerangka punya jarak aman sendiri, jadi jarak antar kerangka = 2× jarak aman)</span>
            </div>

            <div class="grid gap-4 lg:grid-cols-2">
                <section class="rounded-xl border border-gray-200 bg-white p-5">
                    <h3 class="mb-3 font-semibold text-gray-700">Preview kerangka</h3>
                    <div v-if="boxErrors.length || !isSafeMarginValid"
                        class="rounded-lg border border-dashed border-red-200 bg-red-50 px-4 py-6 text-sm text-red-500">
                        <div v-for="error in boxErrors" :key="error">{{ error }}</div>
                        <div v-if="!isSafeMarginValid">Jarak aman tidak boleh kosong atau negatif.</div>
                    </div>
                    <FramePreview v-else-if="dieline" :dieline="dieline" :safeMargin="safeMarginValue" />
                </section>

                <section class="rounded-xl border border-gray-200 bg-white p-5">
                    <div class="mb-3 flex items-center justify-between gap-x-2">
                        <h3 class="font-semibold text-gray-700">Preview plano</h3>
                        <span v-if="layout" class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                            :class="layout.count ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'">
                            {{ layout.count }} kerangka per plano
                        </span>
                    </div>
                    <div v-if="!selectedPlano"
                        class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                        Pilih kertas plano terlebih dahulu
                    </div>
                    <div v-else-if="!layout || !dieline"
                        class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                        Lengkapi ukuran kemasan dan jarak aman
                    </div>
                    <template v-else>
                        <PlanoLayoutPreview :planoWidth="selectedPlano.width" :planoHeight="selectedPlano.length"
                            :dieline="dieline" :layout="layout" :safeMargin="safeMarginValue" />
                        <div class="mt-2 flex flex-col gap-0.5 text-xs text-gray-500">
                            <div v-if="!layout.count" class="text-red-500">
                                Kerangka beserta jarak aman tidak muat di plano ini.
                            </div>
                            <template v-else>
                                <div>Horizontal: {{ layout.horizontalCount }} · Vertikal: {{ layout.verticalCount }}</div>
                                <div v-if="unusedArea">
                                    Sisa plano tidak terpakai: {{ formatCm(unusedArea.leftover) }}%
                                    <span class="text-gray-400">
                                        ({{ formatCm(unusedArea.total) }}% jika termasuk jarak aman)
                                    </span>
                                </div>
                                <div v-if="!layout.isExhaustive" class="text-amber-600">
                                    Kerangka sangat kecil, susunan dicari dengan metode cepat sehingga mungkin belum maksimal.
                                </div>
                            </template>
                        </div>
                    </template>
                </section>
            </div>

            <section class="rounded-xl border border-gray-200 bg-white p-5">
                <div class="mb-3 flex items-center justify-between gap-x-4">
                    <div>
                        <h3 class="font-semibold text-gray-700">Teks penawaran</h3>
                        <p class="text-xs text-gray-400">
                            Teks terisi otomatis dan bisa diedit langsung. Mengubah input di atas akan membuat ulang teks.
                        </p>
                    </div>
                    <div class="flex gap-x-2">
                        <Button size="xs" type="tertiary" icon="fal fa-undo-alt" label="Generate ulang"
                            @click="regenerateOfferText" />
                        <Button size="xs" icon="fal fa-copy" label="Copy" @click="useCopyText(offerText)" />
                    </div>
                </div>

                <div class="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    <div class="flex items-center justify-between gap-x-2">
                        <span class="font-medium text-gray-700">Catatan (dari Settings)</span>
                        <a :href="noteSettingsUrl" class="primaryLink" @click.prevent="goToNoteSettings">
                            Ubah catatan di Settings
                        </a>
                    </div>
                    <div class="mt-1 whitespace-pre-line" :class="offerNote ? '' : 'italic text-gray-400'">
                        {{ offerNote || 'Belum ada catatan' }}
                    </div>
                    <div v-if="isOfferNoteOutdated" class="mt-1 text-amber-600">
                        Catatan di teks penawaran berbeda dengan catatan di Settings.
                        <button type="button" class="underline hover:text-amber-700" @click="regenerateOfferText">
                            Generate ulang
                        </button>
                    </div>
                </div>

                <textarea v-model="offerText" rows="18" class="w-full rounded-md border-gray-300 font-mono text-sm" />
            </section>
        </div>
    </div>
</template>
